<script setup lang="ts">
import { computed } from 'vue';
import Tag from 'primevue/tag';

import type { DataTableCellProps } from './templateTypes';

/***
 * template to show status badge (Clear / Unclear)
 */

const _props = defineProps<DataTableCellProps>();

// Use the column field to dynamically access the correct status field
const fieldName = _props.column.field;
const cellData = computed(() => _props.row[fieldName]);

// The raw value from meta, e.g., "Clear" or "Unclear"
// Use meta('rawvalue') to properly unwrap DataWithMeta objects
const statusLabel = computed(() => cellData.value?.meta?.('rawvalue') ?? '');
const statusText = computed(
  () => cellData.value?.meta?.('renderedValue') ?? cellData.value?.meta?.('rawvalue') ?? '',
);

const statusIcon = computed(() => {
  const value = statusLabel.value?.toLowerCase();

  // Clear -> checkmark
  if (value === 'clear' || value === 'clarified') {
    return 'pi pi-check-circle';
  }

  // Ongoing -> clock
  if (value === 'inclarification') {
    return 'pi pi-clock';
  }

  // Unclear -> question mark
  if (value === 'unclear') {
    return 'pi pi-question-circle';
  }

  // Fallback: no icon
  return '';
});

const statusSeverity = computed(() => {
  const value = statusLabel.value?.toLowerCase();
  if (value === 'clear' || value === 'clarified') {
    return 'success';
  }
  if (value === 'unclear') {
    return 'warn';
  }
  return 'secondary';
});
</script>

<template>
  <Tag v-if="statusLabel" :value="statusLabel" :icon="statusIcon" :severity="statusSeverity">
    {{ statusText }}
  </Tag>
</template>
